import { useI18n, type MessageKey } from "@baton/i18n";
import { BatonMark } from "@baton/brand";
import { useState, type ReactNode } from "react";
import { Link } from "react-router";
import { GlideList } from "../sidebar/GlideList.tsx";
import { formatCount } from "../sidebar/Sidebar.tsx";

/** The rail tiles' shared highlight (`GlideList.tsx`): a tile's box at the tile radius, centred in the rail. */
const RAIL_PILL = "inset-x-0 mx-auto w-(--rail-item-width) rounded-(--rail-item-radius) bg-chrome-hover";

/**
 * FR-015-002, FR-015-100, `contracts/screens.md` § The rail and the work surface,
 * `contracts/design-system.md` § The shell: the product's B mark at the rail's head (the
 * rail's 72 px leave no room for the word; 2026-09-28, by the owner's decision: "use the logos
 * everywhere in the platform"), named with the product's name as the rail's one brand text and
 * linking to the Home mode's landing route inside the workspace so the shell stays mounted
 * (the root redirect at `/` sits outside the workspace layout and would unmount it),
 * one tile per available mode, and the bottom cluster with the Help tile, whose menu holds the
 * keyboard shortcuts and Connect AI tools (`HelpMenu.tsx`, 2026-09-30), and the avatar tile that opens
 * the account card (2026-09-17, by the owner's decision: the sidebar's actor card said the same
 * thing twice). A mode tile is a button rather than a
 * link, because it opens the mode's remembered route and so names no fixed destination; the
 * destinations are the mode's sidebar rows. The tile of the mode that owns the current route is
 * raised with the indicator bar on the rail's outer edge. A tile is an upright rectangle, the icon
 * over the mode's word (2026-09-20, by the owner's decision), so the word is the tile's accessible
 * name and no tooltip repeats it; the avatar tile is the avatar over the person's first name, with
 * the full name in its tooltip and its `aria-label` (`RailAccount.tsx`, 2026-09-29). The tiles share one highlight that glides to the
 * hovered tile (`GlideList.tsx`, after Spectrum UI's Tree Nav). The rail itself never changes
 * size and has no flyout of its own (2026-09-17, by the owner's decision: the labelled copy of the
 * same static tiles added nothing); resting on it while the sidebar is collapsed peeks the sidebar
 * (`layout/AppShell.tsx`).
 *
 * The B mark's sheen (2026-09-29, by the owner's review: "if we hover over the logo in the top left
 * rail, it should animate or shimmer or something?"; DESIGN.md § Brand, the one motion the logo has):
 * one soft band of the mark's own ink passes across the B along its diagonal cut, once for each time
 * the pointer comes onto the home link and each time the link takes keyboard focus. A pass plays to
 * its end even when the pointer leaves, and a second entry during it does not restart it, so a quick
 * pass over the rail never flickers. The layer is mounted only while it plays, so the idle rail draws
 * the mark alone; under reduced motion its duration token is zero and nothing shows.
 */
/** What a tile draws of a mode: a workspace mode (`modes/modes.tsx`), or the Tickets tile of a client member's portal shell. */
export interface RailTile {
  readonly id: string;
  readonly label: MessageKey;
  /** A word drawn as it is under the tile in place of `label`: the client's name on a client member's one tile while they hold one portal (028 FR-028-046). */
  readonly title?: string;
  readonly icon: ReactNode;
  /** What waits for the reader in the mode, drawn on the tile while above zero, with its words: the Tickets tile's unread messages (028 FR-028-053). */
  readonly unread?: { readonly count: number; readonly label: string };
}

export interface RailProps<M extends RailTile> {
  readonly modes: readonly M[];
  /** The mode that owns the current route, or null on a route no mode owns. */
  readonly active: M | null;
  /** The landing route the B mark opens: the Home mode's in a workspace, the Tickets landing in a portal shell. */
  readonly homeRoute: string;
  readonly onSelectMode: (mode: M) => void;
  /** 019's Assistant tile above Help (`assistant/AssistantTile.tsx`), absent where the panel cannot open (2026-10-14). */
  readonly assistant?: ReactNode;
  /** The Help tile and its menu (`HelpMenu.tsx`); absent in a client member's portal shell, which offers neither the shortcuts nor the AI tools it holds (028 FR-028-046). */
  readonly help?: ReactNode;
  /** The avatar tile at the rail's bottom, the account card's one trigger (`RailAccount.tsx`). */
  readonly account?: ReactNode;
}

export function Rail<M extends RailTile>({ modes, active, homeRoute, onSelectMode, assistant, help, account }: RailProps<M>) {
  const { t, format } = useI18n();
  const [sheen, setSheen] = useState(false);
  return (
    <nav className="b-rail" aria-label={t("nav.rail")}>
      <div className="b-rail__head">
        <Link
          to={homeRoute}
          className="b-rail__home"
          onPointerEnter={() => {
            setSheen(true);
          }}
          onFocus={(event) => {
            if (event.currentTarget.matches(":focus-visible")) setSheen(true);
          }}
          onAnimationEnd={() => {
            setSheen(false);
          }}
        >
          <BatonMark label={t("app.name")} className="b-rail__mark" sheen={sheen} />
        </Link>
      </div>
      <GlideList className="b-rail__items" pillClassName={RAIL_PILL}>
        {modes.map((mode) => (
          <li key={mode.id}>
            <button
              type="button"
              className="b-rail__item"
              data-glide-row
              aria-current={mode === active ? "true" : undefined}
              onClick={() => {
                onSelectMode(mode);
              }}
            >
              {mode.icon}
              <span className="b-rail__label">{mode.title ?? t(mode.label)}</span>
              {mode.unread !== undefined && mode.unread.count > 0 && (
                <span className="b-rail__unread" aria-label={mode.unread.label}>
                  {formatCount(mode.unread.count, (n) => format.number(n))}
                </span>
              )}
            </button>
          </li>
        ))}
      </GlideList>
      <GlideList className="b-rail__bottom" pillClassName={RAIL_PILL}>
        {assistant !== undefined && <li>{assistant}</li>}
        {help !== undefined && <li>{help}</li>}
        {account !== undefined && <li>{account}</li>}
      </GlideList>
    </nav>
  );
}
